import { Alert, Badge, Button, Modal, SelectField, TextField, type Tone } from '@cartly/ui';
import { useState } from 'react';
import { api, ApiError, errorMessage } from '../../api/client';
import type { OrderRow, Page } from '../../api/types';
import { personName } from '../../app/format';
import { useLoader } from '../../app/hooks';
import { NODE_INFO, outcomeLabel } from './labels';
import type { NodeType } from './graph';

interface TestResult {
  steps: { nodeId: string; type: string; status: string; detail: string }[];
  outcome: string;
  sentToYourNumber: boolean;
}

const STATUS_TONE: Record<string, Tone> = {
  ok: 'success',
  simulated: 'info',
  waiting: 'neutral',
  skipped: 'warning',
  failed: 'critical',
};
const STATUS_LABEL: Record<string, string> = {
  ok: 'sent',
  simulated: 'would run',
  waiting: 'wait skipped',
  skipped: 'skipped',
};

/** Test mode: walk the saved draft against real data, optionally sending to your own number. */
export function TestPanel({
  open,
  flowId,
  tenantId,
  nodeTitle,
  prepare,
  onClose,
}: {
  open: boolean;
  flowId: string;
  tenantId: string | undefined;
  /** Human title of a step id, for the result list. */
  nodeTitle: (id: string, type: string) => string;
  /** Saves unsaved edits first; resolves false when saving failed. */
  prepare: () => Promise<boolean>;
  onClose: () => void;
}) {
  const orders = useLoader(
    () => (open ? api.get<Page<OrderRow>>('/orders?limit=25') : Promise.resolve(null)),
    `${tenantId}:${open}`,
  );
  const [orderId, setOrderId] = useState('');
  const [mine, setMine] = useState('');
  const [pending, setPending] = useState<'dry' | 'send' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<TestResult | null>(null);

  const run = async (send: boolean) => {
    setPending(send ? 'send' : 'dry');
    setError(null);
    setResult(null);
    try {
      if (!(await prepare())) return;
      setResult(
        await api.post<TestResult>(`/flows/${flowId}/test`, {
          ...(orderId ? { orderId } : {}),
          ...(send ? { sendTo: mine.trim() } : {}),
        }),
      );
    } catch (err) {
      if (err instanceof ApiError && err.code === 'flow_invalid') {
        setError('Fix the problems listed in the builder before testing.');
      } else setError(errorMessage(err));
    } finally {
      setPending(null);
    }
  };

  const items = orders.data?.items ?? [];
  return (
    <Modal open={open} title="Test this flow" onClose={onClose}>
      <div className="stack">
        <p className="text-muted">
          Runs your latest saved draft against real data. Waits are skipped and tags are never
          changed. Your customers are never messaged.
        </p>
        <SelectField
          label="Use data from an order (optional)"
          value={orderId}
          onChange={(e) => setOrderId(e.target.value)}
          options={[
            { value: '', label: 'No order' },
            ...items.map((o) => ({
              value: o.id,
              label: `#${o.orderNumber ?? o.externalId} · ${o.currency} ${o.total}${
                o.customer ? ` · ${personName(o.customer)}` : ''
              }`,
            })),
          ]}
          hint="Order and customer details fill in the template parameters and conditions."
        />
        <TextField
          label="Your WhatsApp number (to send a real test)"
          value={mine}
          onChange={(e) => setMine(e.target.value)}
          placeholder="919876543210"
          hint="Send test messages here instead of to the customer. The number needs a recorded opt-in on the WhatsApp page, like any other recipient."
        />
        <div className="row">
          <Button variant="secondary" loading={pending === 'dry'} onClick={() => void run(false)}>
            Dry run (sends nothing)
          </Button>
          <Button
            variant="primary"
            loading={pending === 'send'}
            disabled={mine.replace(/\D/g, '').length < 8}
            onClick={() => void run(true)}
          >
            Send test to my number
          </Button>
        </div>
        {error && <Alert tone="critical">{error}</Alert>}
        {result && (
          <div className="stack">
            <Alert
              tone={result.outcome === 'done' ? 'success' : 'warning'}
              title={outcomeLabel(result.outcome)}
            >
              {result.sentToYourNumber
                ? 'Messages were queued to your number and go out through the normal sending pipeline.'
                : 'Nothing was sent.'}
            </Alert>
            <ol className="step-list">
              {result.steps.map((s) => (
                <li key={s.nodeId} className="step-list__item">
                  <div>
                    <div className="table__primary">
                      {nodeTitle(s.nodeId, s.type) || NODE_INFO[s.type as NodeType]?.title}
                    </div>
                    {s.detail && <div className="table__secondary">{s.detail}</div>}
                  </div>
                  <Badge tone={STATUS_TONE[s.status] ?? 'neutral'}>
                    {STATUS_LABEL[s.status] ?? s.status}
                  </Badge>
                </li>
              ))}
            </ol>
          </div>
        )}
      </div>
    </Modal>
  );
}
